import React from 'react';
import { Pie } from 'recharts';
import { AlertTriangle, Package, TrendingDown, Eye } from 'lucide-react';

const AnunciosDashboard = ({ data = null, jsonUrl = null }) => {
  const [anuncios, setAnuncios] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  // Dados de exemplo (fallback)
  const dadosExemplo = [
    { apelido: 'Mercado Livre', total: 150, sem: 45 },
    { apelido: 'Shopee', total: 200, sem: 60 },
    { apelido: 'Amazon', total: 120, sem: 20 },
    { apelido: 'Magazine Luiza', total: 180, sem: 55 },
  ];

  // Função para carregar dados de uma URL
  const carregarDadosUrl = async (url) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }
      const dados = await response.json();
      setAnuncios(dados);
    } catch (err) {
      setError(`Erro ao carregar dados: ${err.message}`);
      setAnuncios(dadosExemplo); // Fallback para dados de exemplo
    } finally {
      setLoading(false);
    }
  };

// Exemplo de como usar o componente:
const ExemploUso = () => {
  // Opção 1: Dados diretos via props
  const meusDados = [
    { apelido: 'Mercado Livre', total: 150, sem: 45 },
    { apelido: 'Shopee', total: 200, sem: 60 },
    { apelido: 'Amazon', total: 120, sem: 20 }
  ];

  return (
    <div>
      <h2>Formas de usar:</h2>
      
      {/* Método 1: Passar dados diretamente */}
      <AnunciosDashboard data={meusDados} />
      
      {/* Método 2: Carregar de uma URL */}
      {/* <AnunciosDashboard jsonUrl="/api/anuncios" /> */}
      
      {/* Método 3: Sem parâmetros (usa dados de exemplo) */}
      {/* <AnunciosDashboard /> */}
    </div>
  );
};

  // Função para validar estrutura dos dados
  const validarDados = (dados) => {
    if (!Array.isArray(dados)) return false;
    return dados.every(item => 
      Object.prototype.hasOwnProperty.call(item, 'apelido') && 
      Object.prototype.hasOwnProperty.call(item, 'total') && 
      Object.prototype.hasOwnProperty.call(item, 'sem') &&
      typeof item.total === 'number' &&
      typeof item.sem === 'number'
    );
  };

  // useEffect para carregar dados
  React.useEffect(() => {
    if (data) {
      // Opção 1: Dados passados diretamente via props
      if (validarDados(data)) {
        setAnuncios(data);
      } else {
        setError('Formato de dados inválido');
        setAnuncios(dadosExemplo);
      }
    } else if (jsonUrl) {
      // Opção 2: URL para buscar JSON
      carregarDadosUrl(jsonUrl);
    } else {
      // Opção 3: Usar dados de exemplo
      setAnuncios(dadosExemplo);
    }
  }, [data, jsonUrl]);

  const totalAnuncios = anuncios.reduce((acc, item) => acc + item.total, 0);
  const totalSemSku = anuncios.reduce((acc, item) => acc + item.sem, 0);
  const percentualSemSku = ((totalSemSku / totalAnuncios) * 100).toFixed(1);

  const preparePieData = (anuncio) => [
    { name: 'Com SKU', value: anuncio.total - anuncio.sem, fill: '#10B981' },
    { name: 'Sem SKU', value: anuncio.sem, fill: '#EF4444' }
  ];

  const styles = {
    container: {
      backgroundColor: '#f8fafc',
      borderRadius: '16px',
      padding: '32px',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      border: '1px solid #e2e8f0',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    header: {
      marginBottom: '32px'
    },
    title: {
      fontSize: '28px',
      fontWeight: '700',
      color: '#1e293b',
      marginBottom: '8px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    subtitle: {
      fontSize: '16px',
      color: '#64748b',
      margin: 0
    },
    statsContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '16px',
      marginBottom: '32px'
    },
    statCard: {
      backgroundColor: 'white',
      padding: '20px',
      borderRadius: '12px',
      border: '1px solid #e2e8f0',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    statIcon: {
      padding: '8px',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    statContent: {
      flex: 1
    },
    statValue: {
      fontSize: '24px',
      fontWeight: '700',
      margin: 0,
      marginBottom: '4px'
    },
    statLabel: {
      fontSize: '14px',
      color: '#64748b',
      margin: 0
    },
    chartsContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: '24px',
      marginTop: '24px'
    },
    chartCard: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '24px',
      border: '1px solid #e2e8f0',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
      transition: 'all 0.2s ease',
      cursor: 'pointer'
    },
    chartTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#1e293b',
      marginBottom: '16px',
      textAlign: 'center',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px'
    },
    chartWrapper: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '250px'
    },
    chartStats: {
      marginTop: '16px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontSize: '14px',
      color: '#64748b'
    },
    progressBar: {
      width: '100%',
      height: '6px',
      backgroundColor: '#f1f5f9',
      borderRadius: '3px',
      overflow: 'hidden',
      marginTop: '8px'
    },
    progressFill: {
      height: '100%',
      backgroundColor: '#EF4444',
      borderRadius: '3px',
      transition: 'width 0.3s ease'
    },
    subInfo: {
      fontSize: '12px',
      color: '#6b7280',
      marginTop: '4px',
      fontWeight: '500'
    },
    alertBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      backgroundColor: percentualSemSku > 30 ? '#FEF2F2' : percentualSemSku > 15 ? '#FEF3C7' : '#F0FDF4',
      color: percentualSemSku > 30 ? '#DC2626' : percentualSemSku > 15 ? '#D97706' : '#16A34A',
      padding: '4px 8px',
      borderRadius: '6px',
      fontSize: '12px',
      fontWeight: '500'
    }
  };

  // Componente de loading
  if (loading) {
    return (
      <div style={{...styles.container, textAlign: 'center', padding: '60px'}}>
        <div style={{marginBottom: '16px'}}>
          <Package size={48} color="#3b82f6" />
        </div>
        <h3 style={{color: '#64748b'}}>Carregando dados...</h3>
      </div>
    );
  }

  // Componente de erro
  const ErrorMessage = () => (
    <div style={{
      backgroundColor: '#fef2f2',
      border: '1px solid #fecaca',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '24px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      color: '#dc2626'
    }}>
      <AlertTriangle size={20} />
      <span>{error}</span>
    </div>
  );

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>
          <Package size={32} color="#3b82f6" />
          Anúncios sem SKU
        </h1>
        <p style={styles.subtitle}>
          Monitore a qualidade dos seus anúncios em diferentes marketplaces
        </p>
      </div>

      {error && <ErrorMessage />}

      <div style={styles.statsContainer}>
        <div style={styles.statCard}>
          <div style={{...styles.statIcon, backgroundColor: '#dbeafe', color: '#3b82f6'}}>
            <Eye size={20} />
          </div>
          <div style={styles.statContent}>
            <p style={{...styles.statValue, color: '#3b82f6'}}>{totalAnuncios}</p>
            <p style={styles.statLabel}>Total de Anúncios</p>
            <div style={styles.subInfo}>
              <span>Em {anuncios.length} marketplaces</span>
            </div>
          </div>
        </div>

        <div style={styles.statCard}>
          <div style={{...styles.statIcon, backgroundColor: '#dcfce7', color: '#16a34a'}}>
            <Package size={20} />
          </div>
          <div style={styles.statContent}>
            <p style={{...styles.statValue, color: '#16a34a'}}>{totalAnuncios - totalSemSku}</p>
            <p style={styles.statLabel}>Com SKU</p>
            <div style={styles.subInfo}>
              <span>✓ {(((totalAnuncios - totalSemSku) / totalAnuncios) * 100).toFixed(1)}% completos</span>
            </div>
          </div>
        </div>

        <div style={styles.statCard}>
          <div style={{...styles.statIcon, backgroundColor: '#fee2e2', color: '#dc2626'}}>
            <AlertTriangle size={20} />
          </div>
          <div style={styles.statContent}>
            <p style={{...styles.statValue, color: '#dc2626'}}>{totalSemSku}</p>
            <p style={styles.statLabel}>Sem SKU</p>
            <div style={styles.subInfo}>
              <span>⚠️ Precisam de atenção</span>
            </div>
          </div>
        </div>

        <div style={styles.statCard}>
          <div style={{...styles.statIcon, backgroundColor: '#fef3c7', color: '#d97706'}}>
            <TrendingDown size={20} />
          </div>
          <div style={styles.statContent}>
            <p style={{...styles.statValue, color: '#d97706'}}>{percentualSemSku}%</p>
            <p style={styles.statLabel}>Taxa de Erro</p>
            <div style={styles.subInfo}>
              <span style={styles.alertBadge}>
                {percentualSemSku > 30 ? <AlertTriangle size={12} /> : '✓'}
                {percentualSemSku > 30 ? 'Crítico' : percentualSemSku > 15 ? 'Atenção' : 'Ótimo'}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div style={styles.chartsContainer}>
        {anuncios.map((anuncio) => {
          const percentualSem = ((anuncio.sem / anuncio.total) * 100).toFixed(1);
          const percentualCom = (100 - percentualSem).toFixed(1);
          
          // Determinar status do marketplace
          const getStatus = (percentual) => {
            if (percentual > 30) return { cor: '#dc2626', texto: 'Crítico', icon: '🔴' };
            if (percentual > 15) return { cor: '#d97706', texto: 'Atenção', icon: '🟡' };
            return { cor: '#16a34a', texto: 'Ótimo', icon: '🟢' };
          };
          
          const status = getStatus(percentualSem);
          
          return (
            <div 
              key={anuncio.apelido}
              style={{
                ...styles.chartCard,
                ':hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                }
              }}
            >
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px'}}>
                <h5 style={{...styles.chartTitle, margin: 0, justifyContent: 'flex-start'}}>
                  <Package size={18} />
                  {anuncio.apelido}
                </h5>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: status.cor
                }}>
                  <span>{status.icon}</span>
                  {status.texto}
                </div>
              </div>
              
              <div style={styles.chartWrapper}>
                <Pie
                  data={preparePieData(anuncio)}
                  width={200}
                  height={200}
                  outerRadius={80}
                  innerRadius={40}
                  paddingAngle={2}
                  dataKey="value"
                />
              </div>

              <div style={{marginTop: '16px'}}>
                {/* Estatísticas detalhadas */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '12px',
                  marginBottom: '12px'
                }}>
                  <div style={{textAlign: 'center', padding: '8px', backgroundColor: '#f8fafc', borderRadius: '6px'}}>
                    <div style={{fontSize: '18px', fontWeight: '700', color: '#16a34a'}}>{anuncio.total - anuncio.sem}</div>
                    <div style={{fontSize: '11px', color: '#64748b'}}>Com SKU ({percentualCom}%)</div>
                  </div>
                  <div style={{textAlign: 'center', padding: '8px', backgroundColor: '#fef2f2', borderRadius: '6px'}}>
                    <div style={{fontSize: '18px', fontWeight: '700', color: '#dc2626'}}>{anuncio.sem}</div>
                    <div style={{fontSize: '11px', color: '#64748b'}}>Sem SKU ({percentualSem}%)</div>
                  </div>
                </div>

                {/* Barra de progresso */}
                <div style={styles.progressBar}>
                  <div 
                    style={{
                      ...styles.progressFill,
                      width: `${percentualSem}%`,
                      backgroundColor: status.cor
                    }}
                  />
                </div>

                {/* Informações adicionais */}
                <div style={{
                  marginTop: '12px',
                  fontSize: '11px',
                  color: '#64748b',
                  textAlign: 'center'
                }}>
                  {anuncio.sem > 0 && (
                    <span>📝 {anuncio.sem} anúncio{anuncio.sem > 1 ? 's' : ''} precisam de SKU</span>
                  )}
                  {anuncio.sem === 0 && (
                    <span>🎉 Todos os anúncios estão completos!</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AnunciosDashboard;